/**
 * An AI call campaign without a call flow map: start from a ready-made one.
 *
 * The three maps come from the server (`GET /flow/templates`), where a test
 * holds each of them to the same check a call runs, so a picked template can be
 * activated as it is and edited from there. "Start empty" draws the CRM's
 * three-step starter, which the check then asks to finish (its end names no
 * outcome yet). Somebody who may not edit is told the campaign talks by its
 * goal, which is what a campaign without a map does.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Workflow } from "lucide-react";
import { Button } from "@wa/ui";
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { useT } from "../../../../../i18n/I18nProvider";
import { CALL_FLOW_TEMPLATE_IDS, listCallFlowTemplates, type CallFlowTemplate } from "../../../../../lib/aiCallFlow";
import { Btn, Card, EmptyState } from "../../../../ui";
import { crmStarterDoc } from "../../../_shared/flowmap";

export function FlowTemplatePicker({ canEdit, start }: { canEdit: boolean; start: (doc: FlowMapDoc) => void }) {
  const t = useT();
  const [templates, setTemplates] = useState<CallFlowTemplate[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    let alive = true;
    listCallFlowTemplates().then(
      (res) => {
        if (!alive) return;
        // Only the ids this build can word, in the order it knows them.
        const known = CALL_FLOW_TEMPLATE_IDS.flatMap((id) => res.templates.filter((tpl) => tpl.id === id));
        setTemplates(known);
      },
      () => {
        if (alive) setFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [canEdit]);

  if (!canEdit) {
    return (
      <Card>
        <EmptyState icon={Workflow} title={t("aiCalls.flow.title")} body={t("aiCalls.flow.templates.readOnly")} />
      </Card>
    );
  }

  return (
    <Card>
      <EmptyState
        icon={Workflow}
        title={t("aiCalls.flow.templates.title")}
        body={t("aiCalls.flow.templates.body")}
        action={
          <div className="w-full max-w-3xl space-y-3">
            {templates === null && !failed ? (
              <div className="flex justify-center py-2 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            ) : null}
            {failed ? <p className="crm-sub text-sm">{t("aiCalls.flow.templates.failed")}</p> : null}
            {templates && templates.length > 0 ? (
              <div className="grid gap-3 text-left sm:grid-cols-3">
                {templates.map((tpl) => (
                  <Button
                    key={tpl.id}
                    variant="outline"
                    onClick={() => start(tpl.doc)}
                    className="h-auto flex-col items-start gap-1 whitespace-normal rounded-xl px-4 py-3 text-left"
                  >
                    <span className="text-sm font-semibold">{t(`aiCalls.flow.templates.${tpl.id}.title`)}</span>
                    <span className="crm-sub text-xs font-normal leading-relaxed">{t(`aiCalls.flow.templates.${tpl.id}.body`)}</span>
                  </Button>
                ))}
              </div>
            ) : null}
            <div className="flex justify-center">
              <Btn onClick={() => start(crmStarterDoc(t))}>
                <Plus className="size-4" />
                {t("aiCalls.flow.templates.blank")}
              </Btn>
            </div>
          </div>
        }
      />
    </Card>
  );
}
